import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Loader2, SendHorizonal } from "lucide-react";
import { AppShell, type AppShellContext } from "@/components/app/AppShell";
import { api } from "@/lib/api";

type Bucket = "in_progress" | "succeeded" | "failed" | "needs_help";

type Takedown = {
  id: string;
  reviewId: string;
  bucket: Bucket;
  actionStatus: string;
  agentRunStatus: string | null;
  agentRunErrorCategory: string | null;
  amazonCaseId: string | null;
  submittedAt: string | null;
  removedAt: string | null;
  asin: string;
  productTitle: string | null;
  brandId: string | null;
  brandName: string | null;
  rating: number;
  reviewTitle: string | null;
  reviewBody: string;
  reviewerName: string | null;
};

type Counts = Record<Bucket, number>;

type Tab = "in_progress" | "succeeded" | "failed";

const TAB_LABEL: Record<Tab, string> = {
  in_progress: "In progress",
  succeeded: "Removed",
  failed: "Rejected",
};

function brandRegistryCaseUrl(caseId: string) {
  return `https://brandregistry.amazon.com/gp/case-dashboard/view-case.html?caseID=${encodeURIComponent(
    caseId,
  )}`;
}

export default function DashboardTakedowns() {
  return <AppShell>{(ctx) => <Inner ctx={ctx} />}</AppShell>;
}

function Inner({ ctx }: { ctx: AppShellContext }) {
  const [tab, setTab] = useState<Tab>("in_progress");
  const [data, setData] = useState<{ takedowns: Takedown[]; counts: Counts } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    api<{ takedowns: Takedown[]; counts: Counts }>(`/customer/takedowns`).then((res) => {
      if (cancelled) return;
      if (res.data) setData(res.data);
      else setData({ takedowns: [], counts: { in_progress: 0, succeeded: 0, failed: 0, needs_help: 0 } });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Customer-facing: fold the internal "needs_help" bucket into "In progress".
  const visibleCounts = data
    ? {
        in_progress: data.counts.in_progress + data.counts.needs_help,
        succeeded: data.counts.succeeded,
        failed: data.counts.failed,
      }
    : { in_progress: 0, succeeded: 0, failed: 0 };

  const visible = data
    ? data.takedowns.filter((t) => {
        if (tab === "in_progress") return t.bucket === "in_progress" || t.bucket === "needs_help";
        return t.bucket === tab;
      })
    : null;

  if (ctx.brands.length === 0) {
    return (
      <div className="mx-auto w-full max-w-3xl px-6 py-16 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
          No takedowns yet
        </h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Add a brand and authorize a takedown to start the pipeline.
        </p>
        <Link
          to="/dashboard/brands/new"
          className="mt-6 inline-flex rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
        >
          + Add a brand
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-8">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
        Takedowns
      </h1>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Reviews submitted to Amazon or authorized for Reputely to submit.
      </p>

      <div className="mt-6 flex flex-wrap gap-1.5">
        {(Object.keys(TAB_LABEL) as Tab[]).map((t) => {
          const active = t === tab;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-medium ring-1 ring-inset ${
                active
                  ? "bg-slate-900 text-white ring-slate-900 dark:bg-slate-100 dark:text-slate-900 dark:ring-slate-100"
                  : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700 dark:hover:bg-slate-800"
              }`}
            >
              {TAB_LABEL[t]}
              <span
                className={`rounded-full px-1.5 py-0.5 text-xs ${
                  active
                    ? "bg-white/20 text-white dark:bg-slate-900/20 dark:text-slate-900"
                    : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                }`}
              >
                {visibleCounts[t]}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-6 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
        {visible === null && (
          <div className="flex items-center gap-2 px-5 py-6 text-sm text-slate-500 dark:text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {visible && visible.length === 0 && (
          <div className="px-5 py-12 text-center">
            <SendHorizonal className="mx-auto h-6 w-6 text-slate-400" />
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
              No takedowns in this state.
            </p>
          </div>
        )}
        {visible?.map((t) => <TakedownRow key={t.id} item={t} tab={tab} />)}
      </div>
    </div>
  );
}

function TakedownRow({ item, tab }: { item: Takedown; tab: Tab }) {
  return (
    <div className="flex items-start gap-4 px-5 py-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3">
          <span className="font-medium text-slate-900 dark:text-slate-100">
            {item.productTitle || item.asin}
          </span>
          <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400">
            {item.asin}
          </span>
          {item.brandName && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              · {item.brandName}
            </span>
          )}
        </div>
        <p className="mt-1 line-clamp-2 text-sm text-slate-600 dark:text-slate-400">
          {item.reviewTitle ? `"${item.reviewTitle}" — ` : ""}
          {item.reviewBody.slice(0, 180)}
          {item.reviewBody.length > 180 ? "…" : ""}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <StatusPill tab={tab} />
        {item.submittedAt && (
          <span className="text-[11px] text-slate-500 dark:text-slate-400">
            Submitted {new Date(item.submittedAt).toLocaleDateString()}
          </span>
        )}
        {item.amazonCaseId && (
          <a
            href={brandRegistryCaseUrl(item.amazonCaseId)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 hover:text-slate-950 dark:text-slate-300 dark:hover:text-white"
          >
            Case {item.amazonCaseId}
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        )}
        {item.removedAt && tab === "succeeded" && (
          <span className="text-[11px] text-slate-500 dark:text-slate-400">
            Removed {new Date(item.removedAt).toLocaleDateString()}
          </span>
        )}
      </div>
    </div>
  );
}

function StatusPill({ tab }: { tab: Tab }) {
  if (tab === "succeeded") {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-900">
        Removed
      </span>
    );
  }
  if (tab === "failed") {
    return (
      <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900">
        Rejected
      </span>
    );
  }
  // in_progress (also covers needs_help, framed as in-progress to customers)
  return (
    <span className="inline-flex items-center rounded-full bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-700 ring-1 ring-inset ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-900">
      In progress
    </span>
  );
}
